import { type JSX } from "react";
import { Link } from "react-router-dom";
import {
  RunData,
  SampleData,
} from "@via/pages/sample-trackers/samples/sample-explorer.api.tsx";
import { formatFieldValue } from "@via/pages/sample-trackers/samples/utils/field-helpers.tsx";
import { TruncatedText } from "@via/components/truncated-text.tsx";
import { getRunStatusLabel } from "@via/pages/projects/runs/utils/helpers.tsx";
import RunPipelineLink from "@via/pages/projects/runs/run-pipeline-link.tsx";

interface SampleRowExpandedContentProps {
  sample: SampleData;
  runReportFields?: Record<string, { name: string; type: string }[]>;
  viewportWidth?: number;
}

// Helper function to get report value for a specific run, report path, and field
const getReportValue = (
  run: RunData,
  reportPath: string,
  fieldName: string
): number | null => {
  if (!run.reports) return null;
  const report = run.reports[reportPath];
  if (!report) return null;
  return report[fieldName] ?? null;
};

// Helper function to get non-empty files
const getFiles = (sample: SampleData): string[] => {
  return [sample.file1, sample.file2, sample.file3, sample.file4].filter(
    file => file && file.trim() !== ""
  );
};

export default function SampleRowExpandedContent({
  sample,
  runReportFields,
  viewportWidth,
}: SampleRowExpandedContentProps): JSX.Element {
  // Get report paths and their fields from runReportFields
  const reportEntries = runReportFields ? Object.entries(runReportFields) : [];

  return (
    <div
      className="sticky left-0 box-border p-4 bg-gray-50 border-t border-gray-200"
      style={
        viewportWidth && viewportWidth > 0
          ? { width: `${viewportWidth}px`, maxWidth: `${viewportWidth}px` }
          : undefined
      }>
      <div className="space-y-4">
        {/* Run Details Sub-Table */}
        {sample.runData.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-sm font-medium text-gray-900">
                Run Details ({sample.runData.length} runs)
              </span>
            </div>
            <div className="w-full overflow-x-auto border border-gray-200 rounded-md bg-white">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      rowSpan={2}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 tracking-wider border-r border-gray-200">
                      Run ID
                    </th>
                    <th
                      rowSpan={2}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 tracking-wider border-r border-gray-200">
                      Status
                    </th>
                    <th
                      rowSpan={2}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 tracking-wider border-r border-gray-200">
                      Name
                    </th>
                    <th
                      rowSpan={2}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 tracking-wider border-r border-gray-200">
                      Pipeline
                    </th>
                    <th
                      rowSpan={2}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 tracking-wider border-r border-gray-200">
                      Version
                    </th>
                    {reportEntries.map(([reportPath, fields]) => (
                      <th
                        key={reportPath}
                        colSpan={fields.length}
                        className="px-4 py-2 text-left text-xs font-medium text-gray-900 tracking-wider border-l border-gray-300">
                        <div className="flex flex-col">
                          <span className="font-semibold">REPORTS</span>
                          <span className="text-gray-500 text-[12px] font-normal mt-0.5">
                            {reportPath}
                          </span>
                        </div>
                      </th>
                    ))}
                  </tr>
                  {/* Second header row with field names */}
                  <tr>
                    {reportEntries.map(([reportPath, fields]) =>
                      fields.map(field => (
                        <th
                          key={`${reportPath}_${field.name}`}
                          className="px-4 py-2 text-left text-xs font-medium text-gray-500 tracking-wider border-l border-gray-200">
                          {field.name}
                        </th>
                      ))
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sample.runData.map((run, index) => (
                    <tr key={run.runId || index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 border-r border-gray-200">
                        <Link
                          to={`/runs/${run.runId}`}
                          className="via-link truncate"
                          target="_blank"
                          rel="noreferrer">
                          {run.runId}
                        </Link>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 border-r border-gray-200">
                        {getRunStatusLabel(String(run.runStatus))}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 border-r border-gray-200">
                        <Link
                          to={`/runs/${run.runId}`}
                          className="via-link truncate"
                          target="_blank"
                          rel="noreferrer">
                          <TruncatedText
                            text={run.runName}
                            maxLength={40}
                            id={`run-tooltip-${run.runId}-${run.runName}`}
                          />
                        </Link>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 border-r border-gray-200">
                        {/* A run whose subject is a process points at the one
                            node pipeline synthesized for it, which the pipeline
                            editor refuses to open, so this cell must not link
                            there. RunPipelineLink owns that rule for every
                            surface that renders a run's pipeline reference; do
                            not re-inline a router link here. */}
                        <RunPipelineLink
                          pipelineId={run.pipelineId}
                          pipelineName={run.pipelineName}
                          subjectType={run.subjectType}
                          openInNewTab
                        />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap border-r border-gray-200">
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800">
                          {run.pipelineVersion}
                        </span>
                      </td>
                      {reportEntries.map(([reportPath, fields]) =>
                        fields.map(field => {
                          const value = getReportValue(
                            run,
                            reportPath,
                            field.name
                          );
                          return (
                            <td
                              key={`${reportPath}_${field.name}`}
                              className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900 border-l border-gray-200">
                              {formatFieldValue(value, field.type)}
                            </td>
                          );
                        })
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Files Section */}
        {getFiles(sample).length > 0 && (
          <div className="pt-2">
            <div className="text-sm font-medium text-gray-900 mb-2 uppercase tracking-wide">
              FILES
            </div>
            <div className="space-y-1 bg-white p-3 rounded-md border border-gray-200">
              {getFiles(sample).map((file, index) => (
                <div
                  key={index}
                  className="text-sm text-gray-600 font-mono break-all">
                  {file}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
